import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export type TabItem = {
  value: string;
  label: ReactNode;
  icon?: IconName | string;
  badge?: ReactNode;
};

export type TabsProps = {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  variant?: 'underline' | 'pill';
  style?: CSSProperties;
};

/** Tabs — segmented navigation, 'underline' or 'pill'. */
export function Tabs({ items = [], value, defaultValue, onChange, variant = 'underline', style }: TabsProps) {
  const isControlled = value !== undefined;
  const [inner, setInner] = useState(defaultValue ?? items[0]?.value);
  const current = isControlled ? value : inner;

  const select = (val: string) => {
    if (!isControlled) setInner(val);
    onChange?.(val);
  };

  const pill = variant === 'pill';

  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        gap: pill ? 'var(--space-1)' : 'var(--space-5)',
        padding: pill ? 'var(--space-1)' : 0,
        background: pill ? 'var(--surface-sunken)' : 'transparent',
        borderRadius: pill ? 'var(--radius-pill)' : 0,
        borderBottom: pill ? 'none' : '1px solid var(--border)',
        ...style,
      }}
    >
      {items.map((it) => {
        const active = it.value === current;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => select(it.value)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--fw-bold)' as unknown as number,
              transition:
                'color var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard)',
              ...(pill
                ? {
                    padding: '0 var(--space-4)',
                    height: '34px',
                    borderRadius: 'var(--radius-pill)',
                    background: active ? 'var(--surface)' : 'transparent',
                    color: active ? 'var(--brand)' : 'var(--text-muted)',
                    boxShadow: active ? 'var(--shadow-xs)' : 'none',
                  }
                : {
                    padding: 'var(--space-3) 0',
                    background: 'transparent',
                    color: active ? 'var(--text)' : 'var(--text-subtle)',
                    borderBottom: `2px solid ${active ? 'var(--brand)' : 'transparent'}`,
                    marginBottom: -1,
                  }),
            }}
          >
            {it.icon ? <Icon name={it.icon} size={16} /> : null}
            {it.label}
            {it.badge != null ? (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-2xs)',
                  background: active ? 'var(--brand-subtle)' : 'var(--sand-200)',
                  color: active ? 'var(--brand)' : 'var(--text-muted)',
                  borderRadius: 'var(--radius-pill)',
                  padding: '1px 7px',
                }}
              >
                {it.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
